import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { XeroReauthorizeModalComponent } from './xero-reauthorize-modal/xero-reauthorize-modal.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-admin-utilities',
  templateUrl: './admin-utilities.component.html',
  styleUrls: ['./admin-utilities.component.css'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatSelectModule, MatFormFieldModule, MatExpansionModule, MatTableModule, MatSortModule, MatCheckboxModule, MatDialogModule, FormsModule]
})
export class AdminUtilitiesComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('alertsSort', { read: MatSort }) alertsSort!: MatSort;
  @ViewChild('invoicesSort', { read: MatSort }) invoicesSort!: MatSort;

  userId = sessionStorage.getItem('UserId');

  isProcessing = false;
  processingTask = '';

  // Email batch progress
  emailSessionId: string | null = null;
  emailTotal = 0;
  emailSent = 0;
  emailFailed = 0;
  emailRemaining = 0;
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  private alertPollFailures = 0;

  // Invoice batch progress
  invoiceSessionId: string | null = null;
  invoiceTotal = 0;
  invoiceSent = 0;
  invoiceSkipped = 0;
  invoiceRemaining = 0;
  invoiceCurrentItem: string | null = null;
  invoiceStatusMessage: string | null = null;
  private invoicePollInterval: ReturnType<typeof setInterval> | null = null;
  private invoicePollFailures = 0;
  
  // Invoice selection
  selectedInvoiceIds: number[] = [];
  // True from the moment "Send" is clicked until a fresh preview is loaded. Kept true after the
  // batch finishes so a user who steps away mid-run (or after completion) can't resubmit the same
  // selection and create duplicate invoices - only previewInvoices() clears it.
  invoicesLocked = false;
  private readonly invoiceBatchStorageKey = 'adminUtilities.invoiceBatchState';
  overrideInvoiceDate = false;
  invoiceDateOverride = '';
  private shiftPressed = false;
  private lastInvoiceCheckedId: number | null = null;

  // Preview data
  alertsDataSource = new MatTableDataSource<any>([]);
  invoicesDataSource = new MatTableDataSource<any>([]);
  showAlertsPreview = false;
  showInvoicesPreview = false;
  loadingAlertsPreview = false;
  loadingInvoicesPreview = false;

  // Table column definitions
  alertsColumns = ['clientName', 'subscriptionName', 'contactName', 'email', 'notificationCountInAlert'];
  invoicesColumns = ['select', 'name', 'serviceTypeName', 'startDate', 'invoiceFrequency', 'lastInvoiceDate', 'daysSinceLastInvoice', 'notificationCount', 'paymentCancelledNotificationCount'];

  // Test mode settings
  alertsTestMode = false;
  invoicesTestMode = false;
  testEmailSendToAddress: string | null = null;
  testFinanceContactId: string | null = null;

  constructor(private http: HttpClient, private toastr: ToastrService, private dialog: MatDialog) {
    this.loadTestModeSettings();
  }

  ngOnInit(): void {
    this.restoreInvoiceBatchState();
  }

  ngAfterViewChecked(): void {
    if (this.alertsSort && !this.alertsDataSource.sort) {
      this.alertsDataSource.sort = this.alertsSort;
    }
    if (this.invoicesSort && !this.invoicesDataSource.sort) {
      this.invoicesDataSource.sort = this.invoicesSort;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopInvoicePolling();
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Shift') this.shiftPressed = true;
  }

  @HostListener('window:keyup', ['$event'])
  onWindowKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Shift') this.shiftPressed = false;
  }

  private loadTestModeSettings(): void {
    this.http.get<any>('charitycentral/GetTestModeSettings').subscribe({
      next: (settings) => {
        this.alertsTestMode = settings.alertsTestMode;
        this.invoicesTestMode = settings.invoicesTestMode;
        this.testEmailSendToAddress = settings.testEmailSendToAddress ?? null;
        this.testFinanceContactId = settings.testFinanceContactId ?? null;
      },
      error: (error) => {
        console.error('Error loading test mode settings:', error);
      }
    });
  }

  runNotifications(): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      height: '240px',
      panelClass: 'run-alerts-confirm-dialog',
      data: {
        title: 'Run Alerts',
        message: 'Are you sure?',
        confirmText: 'Yes',
        cancelText: 'No'
      }
    });

    ref.afterClosed().subscribe(confirmed => {
      if (confirmed !== true) return;

      this.isProcessing = true;
      this.processingTask = 'Starting Alerts';
      this.emailSessionId = null;
      this.emailTotal = this.alertsDataSource.data.length; // use preview count as initial estimate
      this.emailSent = 0;
      this.emailFailed = 0;
      this.emailRemaining = this.emailTotal;

      this.http.get<{ sessionId: string }>(`charitycentral/RunScheduledAlertBatch?UserId=${this.userId}`).subscribe({
        next: (response) => {
          this.emailSessionId = response.sessionId;
          this.processingTask = 'Sending Alerts';
          this.startPolling();
        },
        error: (error) => {
          this.isProcessing = false;
          this.toastr.error(`Error starting alerts: ${error.message}`, 'Alerts Failed', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
        }
      });
    });
  }

  private startPolling(): void {
    this.alertPollFailures = 0;
    this.pollInterval = setInterval(() => {
      if (!this.emailSessionId) return;
      this.http.get<any>(`charitycentral/GetAlertBatchProgress?sessionId=${this.emailSessionId}`).subscribe({
        next: (progress) => {
          this.alertPollFailures = 0;
          if (progress.total > 0) this.emailTotal = progress.total;
          this.emailSent = progress.sent;
          this.emailFailed = progress.failed;
          this.emailRemaining = progress.remaining;

          if (progress.isComplete) {
            this.stopPolling();
            this.isProcessing = false;
            if (progress.errorMessage) {
              this.toastr.error(`Alerts failed: ${progress.errorMessage}`, 'Alerts Failed', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
            } else {
              const msg = progress.failed > 0
                ? `Alerts completed: ${progress.sent} sent, ${progress.failed} failed`
                : `Alerts completed: ${progress.sent} emails sent successfully`;
              this.toastr.success(msg, '', { timeOut: 8000 });
            }
          }
        },
        error: () => {
          this.alertPollFailures++;
          if (this.alertPollFailures >= 5) {
            this.stopPolling();
            this.isProcessing = false;
            this.toastr.error(
              `Lost contact with the alert batch after ${this.emailSent} sent — the server may have restarted mid-run. Check the logs to confirm how many alerts completed before the interruption.`,
              'Alert Batch Connection Lost',
              { timeOut: 0, extendedTimeOut: 0, closeButton: true }
            );
          }
        }
      });
    }, 3000);
  }

  private stopPolling(): void {
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  sendSelectedInvoices(): void {
    if (this.selectedInvoiceIds.length === 0) {
      this.toastr.info('Please select at least one invoice to send', '', { timeOut: 5000 });
      return;
    }

    this.isProcessing = true;
    this.invoicesLocked = true;
    this.processingTask = 'Starting Invoices';
    this.invoiceSessionId = null;
    this.invoiceTotal = this.selectedInvoiceIds.length;
    this.invoiceSent = 0;
    this.invoiceSkipped = 0;
    this.invoiceRemaining = this.invoiceTotal;
    this.invoiceCurrentItem = null;
    this.invoiceStatusMessage = null;
    this.saveInvoiceBatchState();

    let runUrl = `charitycentral/RunSelectedInvoices?UserId=${this.userId}`;
    if (this.overrideInvoiceDate && this.invoiceDateOverride) {
      runUrl += `&invoiceDate=${encodeURIComponent(this.invoiceDateOverride)}`;
    }

    this.http.post<{ sessionId: string; rejectedSubscriptionIds: number[] }>(runUrl, this.selectedInvoiceIds).subscribe({
      next: (response) => {
        this.invoiceSessionId = response.sessionId;
        this.processingTask = 'Sending Invoices';
        this.saveInvoiceBatchState();
        this.startInvoicePolling();

        if (response.rejectedSubscriptionIds?.length > 0) {
          const count = response.rejectedSubscriptionIds.length;
          this.toastr.warning(
            `${count} of the selected invoice${count !== 1 ? 's were' : ' was'} skipped - no longer available to invoice (likely already processed).`,
            'Some Invoices Skipped',
            { timeOut: 10000 }
          );
        }
      },
      error: (error) => {
        // The request never reached the point of returning a sessionId, so no batch was queued
        // server-side - safe to unlock and let the user retry the same selection.
        this.isProcessing = false;
        this.invoicesLocked = false;
        this.saveInvoiceBatchState();
        this.toastr.error(`Error starting invoices: ${error.error?.message || error.message}`, 'Invoices Failed', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
      }
    });
  }

  private setInvoiceSelected(subscriptionId: number, selected: boolean): void {
    const isSelected = this.selectedInvoiceIds.includes(subscriptionId);
    if (selected && !isSelected) {
      this.selectedInvoiceIds = [...this.selectedInvoiceIds, subscriptionId];
    } else if (!selected && isSelected) {
      this.selectedInvoiceIds = this.selectedInvoiceIds.filter(id => id !== subscriptionId);
    }
  }

  // Shift-click range select: clicking a row's checkbox while Shift is held selects (or
  // deselects, matching whatever this click's own target state is) every row between the last
  // row clicked and this one, in whatever order the table is CURRENTLY sorted/filtered into —
  // not insertion order. The anchor is tracked by id rather than index so it still resolves
  // correctly even if the user re-sorts between the two clicks.
  onInvoiceCheckboxChange(row: any): void {
    const targetState = !this.isInvoiceSelected(row.subscriptionId);

    if (this.shiftPressed && this.lastInvoiceCheckedId !== null) {
      const rows = this.invoicesDataSource.sortData(this.invoicesDataSource.filteredData, this.invoicesDataSource.sort!);
      const anchorIndex = rows.findIndex(r => r.subscriptionId === this.lastInvoiceCheckedId);
      const targetIndex = rows.findIndex(r => r.subscriptionId === row.subscriptionId);

      if (anchorIndex !== -1 && targetIndex !== -1) {
        const start = Math.min(anchorIndex, targetIndex);
        const end = Math.max(anchorIndex, targetIndex);
        for (let i = start; i <= end; i++) {
          this.setInvoiceSelected(rows[i].subscriptionId, targetState);
        }
        this.lastInvoiceCheckedId = row.subscriptionId;
        return;
      }
    }

    this.setInvoiceSelected(row.subscriptionId, targetState);
    this.lastInvoiceCheckedId = row.subscriptionId;
  }

  isInvoiceSelected(subscriptionId: number): boolean {
    return this.selectedInvoiceIds.includes(subscriptionId);
  }

  isAllInvoicesSelected(): boolean {
    return this.invoicesDataSource.data.length > 0 &&
      this.invoicesDataSource.data.every(row => this.selectedInvoiceIds.includes(row.subscriptionId));
  }

  masterToggleInvoices(): void {
    if (this.isAllInvoicesSelected()) {
      this.selectedInvoiceIds = [];
    } else {
      this.selectedInvoiceIds = this.invoicesDataSource.data.map(row => row.subscriptionId);
    }
  }

  formatFrequency(code: string): string {
    switch (code) {
      case 'M': return 'Monthly';
      case 'Q': return 'Quarterly';
      case 'A': return 'Annual';
      default: return code ?? '-';
    }
  }

  private startInvoicePolling(): void {
    this.invoicePollFailures = 0;
    this.invoicePollInterval = setInterval(() => {
      if (!this.invoiceSessionId) return;
      this.http.get<any>(`charitycentral/GetInvoiceBatchProgress?sessionId=${this.invoiceSessionId}`).subscribe({
        next: (progress) => {
          this.invoicePollFailures = 0;
          if (progress.total > 0) this.invoiceTotal = progress.total;
          this.invoiceSent = progress.sent;
          this.invoiceSkipped = progress.skipped;
          this.invoiceRemaining = progress.remaining;
          this.invoiceCurrentItem = progress.currentItem ?? null;
          this.invoiceStatusMessage = progress.statusMessage ?? null;

          if (progress.isComplete) {
            this.stopInvoicePolling();
            this.isProcessing = false;
            // invoicesLocked stays true - these invoices have been sent, so re-preview is required
            // before the same (or any) selection can be sent again.
            this.saveInvoiceBatchState();
            if (progress.errorMessage) {
              const isTokenError = progress.errorMessage.toLowerCase().includes('authorization') ||
                                   progress.errorMessage.toLowerCase().includes('reauthorize');
              if (isTokenError) {
                this.toastr.warning('Xero authorization expired. Please reauthorize to continue.', 'Xero Auth Required', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
                this.openXeroReauthorizeModal();
              } else {
                this.toastr.error(`Invoices failed: ${progress.errorMessage}`, 'Invoices Failed', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
              }
            } else {
              const msg = progress.skipped > 0
                ? `Invoices completed: ${progress.sent} sent, ${progress.skipped} skipped`
                : `Invoices completed: ${progress.sent} invoice${progress.sent !== 1 ? 's' : ''} sent successfully`;
              this.toastr.success(msg, '', { timeOut: 8000 });
            }
          } else {
            this.saveInvoiceBatchState();
          }
        },
        error: () => {
          this.invoicePollFailures++;
          if (this.invoicePollFailures >= 5) {
            this.stopInvoicePolling();
            this.isProcessing = false;
            // Whether the batch is still running server-side is unknown - keep invoicesLocked true
            // so the user can't fire off a duplicate run while it's potentially still in flight.
            this.saveInvoiceBatchState();
            this.toastr.error(
              `Lost contact with the invoice batch after ${this.invoiceSent} sent — the server may have restarted mid-run. Check the logs to confirm how many invoices completed before the interruption.`,
              'Invoice Batch Connection Lost',
              { timeOut: 0, extendedTimeOut: 0, closeButton: true }
            );
          }
        }
      });
    }, 3000);
  }

  // Persists enough state to survive the user navigating away and back (or reloading the page)
  // while a batch is running or has just finished, so the "Send" button stays disabled and their
  // selection is still visible as a reference instead of silently resetting and inviting a
  // duplicate submission.
  private saveInvoiceBatchState(): void {
    if (!this.invoicesLocked) {
      sessionStorage.removeItem(this.invoiceBatchStorageKey);
      return;
    }
    const state = {
      selectedInvoiceIds: this.selectedInvoiceIds,
      selectedInvoiceRows: this.invoicesDataSource.data.filter(row => this.selectedInvoiceIds.includes(row.subscriptionId)),
      isProcessing: this.isProcessing,
      processingTask: this.processingTask,
      invoiceSessionId: this.invoiceSessionId,
      invoiceTotal: this.invoiceTotal,
      invoiceSent: this.invoiceSent,
      invoiceSkipped: this.invoiceSkipped,
      invoiceRemaining: this.invoiceRemaining,
      invoiceCurrentItem: this.invoiceCurrentItem,
      invoiceStatusMessage: this.invoiceStatusMessage
    };
    sessionStorage.setItem(this.invoiceBatchStorageKey, JSON.stringify(state));
  }

  private restoreInvoiceBatchState(): void {
    const raw = sessionStorage.getItem(this.invoiceBatchStorageKey);
    if (!raw) return;

    try {
      const state = JSON.parse(raw);
      this.selectedInvoiceIds = state.selectedInvoiceIds ?? [];
      this.invoicesLocked = true;
      this.processingTask = state.processingTask ?? '';
      this.invoiceSessionId = state.invoiceSessionId ?? null;
      this.invoiceTotal = state.invoiceTotal ?? 0;
      this.invoiceSent = state.invoiceSent ?? 0;
      this.invoiceSkipped = state.invoiceSkipped ?? 0;
      this.invoiceRemaining = state.invoiceRemaining ?? 0;
      this.invoiceCurrentItem = state.invoiceCurrentItem ?? null;
      this.invoiceStatusMessage = state.invoiceStatusMessage ?? null;

      if (Array.isArray(state.selectedInvoiceRows) && state.selectedInvoiceRows.length > 0) {
        this.invoicesDataSource.data = state.selectedInvoiceRows;
        this.showInvoicesPreview = true;
      }

      if (state.isProcessing && this.invoiceSessionId) {
        this.isProcessing = true;
        this.startInvoicePolling();
      }
    } catch {
      sessionStorage.removeItem(this.invoiceBatchStorageKey);
    }
  }

  private stopInvoicePolling(): void {
    if (this.invoicePollInterval !== null) {
      clearInterval(this.invoicePollInterval);
      this.invoicePollInterval = null;
    }
  }

  private openXeroReauthorizeModal(): void {
    this.dialog.open(XeroReauthorizeModalComponent, {
      width: '500px',
      disableClose: true,
      panelClass: 'xero-reauthorize-dialog'
    });
  }

  previewAlerts(): void {
    this.loadingAlertsPreview = true;
    this.alertsDataSource.sort = null;
    this.http.get(`charitycentral/GetAlertsPreview?UserId=${this.userId}`).subscribe({
      next: (data: any) => {
        this.alertsDataSource.data = data;
        this.showAlertsPreview = true;
        this.loadingAlertsPreview = false;
        if (data.length === 0) {
          this.toastr.info('No alerts to preview', '', { timeOut: 5000 });
        }
      },
      error: (error) => {
        this.loadingAlertsPreview = false;
        this.toastr.error(`Error loading alerts preview: ${error.message}`, '', { timeOut: 5000 });
        console.error('Error:', error);
      }
    });
  }

  previewInvoices(): void {
    this.loadingInvoicesPreview = true;
    this.selectedInvoiceIds = [];
    this.invoicesLocked = false;
    sessionStorage.removeItem(this.invoiceBatchStorageKey);
    this.invoicesDataSource.sort = null;

    this.http.get(`charitycentral/GetInvoicesPreview?UserId=${this.userId}`).subscribe({
      next: (data: any) => {
        this.invoicesDataSource.data = data;
        this.showInvoicesPreview = true;
        this.loadingInvoicesPreview = false;
        if (data.length === 0) {
          this.toastr.info('No invoices to preview', '', { timeOut: 5000 });
        }
      },
      error: (error) => {
        this.loadingInvoicesPreview = false;
        this.toastr.error(`Error loading invoices preview: ${error.message}`, '', { timeOut: 5000 });
        console.error('Error:', error);
      }
    });
  }
}
